import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { Users, Clock, AlertCircle, ChevronRight, CheckCircle2, Phone, Mail, Search, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Step della pipeline
const PIPELINE_STEPS = [
  { id: 'anagrafica', nome: 'Anagrafica', colore: 'blue' },
  { id: 'negoziazione', nome: 'Negoziazione', colore: 'yellow' },
  { id: 'documentazione', nome: 'Documentazione', colore: 'purple' },
  { id: 'contratto', nome: 'Contratto', colore: 'green' },
  { id: 'chiuso_vinto', nome: 'Chiuso Vinto', colore: 'green' },
  { id: 'chiuso_perso', nome: 'Chiuso Perso', colore: 'red' }
];

const LeadsPipelinePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState('anagrafica');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at'); // created_at, valore_stimato, nome

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/crm/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (err) {
      console.error('Errore caricamento lead:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStep = async (leadId, newStep) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/crm/leads/${leadId}/step`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pipeline_step: newStep })
      });

      if (response.ok) {
        fetchLeads(); // Ricarica i lead
      }
    } catch (err) {
      console.error('Errore aggiornamento step:', err);
    }
  };

  const getLeadsByStep = (step) => {
    let filteredLeads = leads.filter(lead => lead.pipeline_step === step);
    
    // Applica ricerca
    if (searchTerm) {
      filteredLeads = filteredLeads.filter(lead => 
        lead.nome_cognome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.azienda?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.telefono?.includes(searchTerm)
      );
    }
    
    // Applica ordinamento
    filteredLeads.sort((a, b) => {
      if (sortBy === 'created_at') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'valore_stimato') {
        return (b.valore_stimato || 0) - (a.valore_stimato || 0);
      } else if (sortBy === 'nome') {
        return (a.nome_cognome || '').localeCompare(b.nome_cognome || '');
      }
      return 0;
    });
    
    return filteredLeads;
  };

  const getColorClass = (colore) => {
    const colors = {
      blue: 'bg-blue-500/20 border-blue-500 text-blue-400',
      yellow: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
      purple: 'bg-purple-500/20 border-purple-500 text-purple-400',
      green: 'bg-green-500/20 border-green-500 text-green-400',
      red: 'bg-red-500/20 border-red-500 text-red-400'
    };
    return colors[colore] || colors.blue;
  };

  const getStepColor = (colore) => {
    const colors = {
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      green: 'bg-green-500',
      red: 'bg-red-500'
    };
    return colors[colore] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 pt-48 pb-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Pipeline Vendite</h1>
          <p className="text-gray-400">Gestisci i tuoi lead attraverso le fasi della pipeline</p>
        </div>

        {/* Pipeline Steps */}
        <div className="grid md:grid-cols-6 gap-4 mb-8">
          {PIPELINE_STEPS.map((step, index) => {
            const leadsInStep = getLeadsByStep(step.id);
            const isActive = selectedStep === step.id;
            
            return (
              <button
                key={step.id}
                onClick={() => setSelectedStep(step.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isActive 
                    ? getColorClass(step.colore)
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                <div className={`w-3 h-3 rounded-full ${getStepColor(step.colore)} mx-auto mb-2`}></div>
                <p className="text-sm font-semibold text-center">{step.nome}</p>
                <p className="text-2xl font-bold text-center mt-1">{leadsInStep.length}</p>
              </button>
            );
          })}
        </div>

        {/* Filtri */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">🔍 Cerca</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nome, email, azienda, telefono..."
                className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">📊 Ordina per</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
              >
                <option value="created_at">Data creazione (più recenti)</option>
                <option value="valore_stimato">Valore stimato (più alto)</option>
                <option value="nome">Nome alfabetico</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => { setSearchTerm(''); setSortBy('created_at'); }}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg border border-gray-600 transition-colors"
              >
                ↺ Reset Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Lead Cards for Selected Step */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-400" />
            Lead in: {PIPELINE_STEPS.find(s => s.id === selectedStep)?.nome}
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-400 mt-4">Caricamento...</p>
            </div>
          ) : getLeadsByStep(selectedStep).length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nessun lead in questa fase</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getLeadsByStep(selectedStep).map(lead => (
                <div 
                  key={lead.id} 
                  onClick={() => navigate(`/lead/${lead.id}`)}
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-blue-500 transition-all cursor-pointer hover:shadow-lg"
                >
                  <h3 className="text-lg font-bold text-white mb-2">{lead.nome_cognome}</h3>
                  
                  {lead.azienda && (
                    <p className="text-sm text-gray-400 mb-2">🏢 {lead.azienda}</p>
                  )}
                  
                  <div className="space-y-1 mb-4">
                    {lead.email && (
                      <p className="text-sm text-gray-400 flex items-center">
                        <Mail size={14} className="mr-2" />
                        {lead.email}
                      </p>
                    )}
                    {lead.telefono && (
                      <p className="text-sm text-gray-400 flex items-center">
                        <Phone size={14} className="mr-2" />
                        {lead.telefono}
                      </p>
                    )}
                  </div>

                  {lead.valore_stimato && (
                    <div className="bg-green-500/10 rounded-lg p-2 mb-4">
                      <p className="text-xs text-gray-400">Valore Stimato</p>
                      <p className="text-lg font-bold text-green-400">€ {lead.valore_stimato.toFixed(2)}</p>
                    </div>
                  )}

                  {/* Move to next step buttons */}
                  <div className="flex gap-2">
                    {PIPELINE_STEPS.map((step, index) => {
                      const currentIndex = PIPELINE_STEPS.findIndex(s => s.id === lead.pipeline_step);
                      const stepIndex = PIPELINE_STEPS.findIndex(s => s.id === step.id);
                      
                      if (stepIndex > currentIndex && stepIndex <= currentIndex + 2) {
                        return (
                          <button
                            key={step.id}
                            onClick={() => updateLeadStep(lead.id, step.id)}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${getColorClass(step.colore)} hover:opacity-80`}
                          >
                            → {step.nome}
                          </button>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <p className="text-xs text-gray-500 mt-4">
                    Creato: {new Date(lead.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LeadsPipelinePage;
